import { selectSelectedDoubtCheckPerimeterCameraUniqueKey } from '@redux/global/global.selector';
import { perimeterCamerasAdapter } from '@redux/sensors/perimeterCameras.adapters';
import { RootState } from '@redux/store';
import { createSelector } from '@reduxjs/toolkit';
import { selectParameter } from '@utils/common.utils';
import { isSensorWithSubSensors } from '@utils/map/sensor.utils';
import { isCameraConfigurationType } from '@utils/sensors/camera/camera.utils';
import {
  getSensorUniqueKey,
  getSubSensorConfigsFromParent,
  isSensorOrSubSensorConfigurationOfType,
  isSubSensorUniqueKeys,
} from '@utils/sensors/sensors.utils';
import { uniq } from 'lodash';

import { SensorUniqueKeys } from '@/types/map.types';
import {
  CameraConfiguration,
  DETECTION_SENSORS,
  SensorConfiguration,
  SensorOrSubSensorConfiguration,
} from '@/types/sensor/configuration.types';
import {
  GetSensorOrSubSensorConfigurationWithShorthand,
  SensorTypeWithShorthand,
} from '@/types/sensor/sensorInstallation.types';
import {
  AutomatonCabinet,
  AutomatonSegment,
  AutomatonSensorConfiguration,
  SensorTypeEnum,
  SubCameraConfiguration,
  SubSensorTypeEnum,
} from '@/types/sensor/sensors';
import { PerimeterCamera } from '@/types/sensor/sensors/perimeterCamera.types';

import { sensorsAdapter } from './sensors.adapters';
import { SensorsState } from './sensors.reducer';

function selectSensorsState(state: RootState): SensorsState {
  return state.sensors;
}

const { selectAll: selectAllSensors } = sensorsAdapter.getSelectors(
  (state: RootState) => selectSensorsState(state).sensors,
);

export const SensorSelectors = {
  selectAllSensors,
};

export const selectAppCodes = createSelector(selectSensorsState, (state) => state.appCodes);

export const selectAllDetectionSensors = createSelector(selectAllSensors, (sensors) =>
  sensors.filter((sensor) => DETECTION_SENSORS.includes(sensor.type)),
);

export const selectAllCamerasConfiguration = createSelector(
  selectAllSensors,
  (sensors: SensorConfiguration[]): CameraConfiguration[] => {
    return sensors.filter(isCameraConfigurationType);
  },
);

export const selectAllActiveCamerasConfiguration = createSelector(
  selectAllCamerasConfiguration,
  (cameras: CameraConfiguration[]): CameraConfiguration[] => cameras.filter((camera) => camera.active),
);

export const selectCameraConfigurationByUniqueKey = createSelector(
  [selectAllCamerasConfiguration, selectParameter<string | null>],
  (cameraConfigurations: CameraConfiguration[], uniqueKey: string | null): CameraConfiguration | null =>
    cameraConfigurations.find((cam) => getSensorUniqueKey(cam) === uniqueKey) ?? null,
);

export const selectAllSubCameras = createSelector(
  selectAllCamerasConfiguration,
  (cameras: CameraConfiguration[]): SubCameraConfiguration[] => cameras.flatMap((camera) => camera.subCameras),
);

export const selectAllAutomatons = createSelector(
  selectAllSensors,
  (sensors: SensorConfiguration[]): AutomatonSensorConfiguration[] =>
    sensors.filter((sensor: SensorConfiguration) =>
      isSensorOrSubSensorConfigurationOfType(sensor, SensorTypeEnum.AUTOMATON),
    ),
);

export const selectAutomatonCabinets = createSelector(selectAllAutomatons, (automatons) =>
  automatons.flatMap((automaton: AutomatonSensorConfiguration) => automaton.cabinets),
);

export const selectAutomatonCabinetSegments = createSelector(
  selectAllAutomatons,
  (automatons: AutomatonSensorConfiguration[]): AutomatonSegment[] =>
    automatons.flatMap((automaton: AutomatonSensorConfiguration) =>
      automaton.cabinets.flatMap((cabinet: AutomatonCabinet) => cabinet.segments),
    ),
);

export const selectPerimeterSensors = createSelector(
  [selectAllAutomatons, selectAutomatonCabinets, selectAutomatonCabinetSegments],
  (automatons, cabinets, segments) => [...automatons, ...cabinets, ...segments],
);

export const selectAutomatonCabinetSegmentsBySegmentName = createSelector(
  [selectAutomatonCabinetSegments, selectParameter<string | null>],
  (segments, segmentName) => segments.filter((segment) => segment.name === segmentName),
);

const { selectAll: selectAllPerimeterCameras } = perimeterCamerasAdapter.getSelectors(
  (state: RootState) => selectSensorsState(state).perimeterCameras,
);

const selectPerimeterCameraByUniqueKey = perimeterCamerasAdapter.getSelectors(
  (state: RootState) => selectSensorsState(state).perimeterCameras,
).selectById as (state: RootState, id: string) => PerimeterCamera | undefined;

export const PerimeterCameraSelectors = {
  selectAllPerimeterCameras,
  selectPerimeterCameraByUniqueKey,
};

export const selectSelectedDoubtCheckPerimeterCamera = (state: RootState) => {
  const uniqueKey = selectSelectedDoubtCheckPerimeterCameraUniqueKey(state);
  return uniqueKey ? selectPerimeterCameraByUniqueKey(state, uniqueKey) : null;
};

export const selectPerimeterCamerasBySegmentName = createSelector(
  [selectAutomatonCabinetSegmentsBySegmentName, selectAllPerimeterCameras],
  (segments: AutomatonSegment[], allPerimeterCameras): PerimeterCamera[] => {
    const perimeterCamerasUniqueKeys = uniq(segments.flatMap((s) => s.perimeterCameraUniqueKeys));
    return allPerimeterCameras.filter((pc) =>
      perimeterCamerasUniqueKeys.some((perimUniqueKey) => pc.uniqueKey === perimUniqueKey),
    );
  },
);

export const selectSensorConfigurationByUniqueKey = createSelector(
  [SensorSelectors.selectAllSensors, selectParameter<string | null>],
  (sensorConfigurations, key): SensorConfiguration | null => {
    return sensorConfigurations.find((sensor) => getSensorUniqueKey(sensor) === key) ?? null;
  },
);

export const selectSensorOrSubSensorConfigurationByUniqueKeys = createSelector(
  [SensorSelectors.selectAllSensors, selectParameter<SensorUniqueKeys | null>],
  (sensorConfigurations, uniqueKeys): SensorOrSubSensorConfiguration | null => {
    if (uniqueKeys === null) {
      return null;
    }
    if (isSubSensorUniqueKeys(uniqueKeys)) {
      return (
        sensorConfigurations
          .filter(isSensorWithSubSensors)
          .flatMap((sensor) => getSubSensorConfigsFromParent(sensor))
          .find(
            (subSensorConfiguration) => getSensorUniqueKey(subSensorConfiguration) === uniqueKeys.sensorUniqueKey,
          ) ?? null
      );
    }
    return sensorConfigurations.find((sensor) => getSensorUniqueKey(sensor) === uniqueKeys.sensorUniqueKey) ?? null;
  },
);

export const selectSensorConfigurationsWithShorthand = createSelector(
  [SensorSelectors.selectAllSensors, selectPerimeterSensors],
  (sensors, perimeterSensors): GetSensorOrSubSensorConfigurationWithShorthand<SensorTypeWithShorthand>[] => {
    const excludedSensors = [
      SensorTypeEnum.DRONE_SHIELD,
      SensorTypeEnum.ONVIF,
      SubSensorTypeEnum.ARDRONIS_ANTENNA,
      SubSensorTypeEnum.GENERIC_SUB_SENSOR,
      SubSensorTypeEnum.ROBIN_MAX_RADAR,
      SubSensorTypeEnum.SENID_ANTENNA,
      SubSensorTypeEnum.SKY_EYE_ANTENNA,
      SubSensorTypeEnum.SKY_JACKER_ANTENNA,
      SubSensorTypeEnum.SPEXER_RADAR,
      SensorTypeEnum.AUTOMATON,
    ] as const;
    const ladSensors = sensors.filter(
      (sensor) => !excludedSensors.some((type) => isSensorOrSubSensorConfigurationOfType(sensor, type)),
    );

    return [
      ...ladSensors,
      ...perimeterSensors,
    ] as GetSensorOrSubSensorConfigurationWithShorthand<SensorTypeWithShorthand>[];
  },
);
